import React, { useState } from 'react';
import { Modal, Button, Tag, Empty, message, Divider } from 'antd';
import { CheckCircleOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const PaymentDetailsModal = ({
  visible,
  onClose,
  selectedDate,
  payments = [],
  onMarkAsPaid,
  loading = false,
}) => {
  const [markingAsPaid, setMarkingAsPaid] = useState({});

  const groupedPayments = payments.reduce((acc, payment) => {
    const type = payment.type || 'unknown';
    if (!acc[type]) acc[type] = [];
    acc[type].push(payment);
    return acc;
  }, {});

  const typeLabels = {
    supplier: 'Proveedores',
    tax: 'Impuestos',
    fixed: 'Gastos Fijos',
    variable: 'Gastos Variables',
  };

  const typeIcons = {
    supplier: '🏭',
    tax: '📋',
    fixed: '📅',
    variable: '📊',
  };

  const isPending = (p) => {
    const s = String(p.status || '').toLowerCase();
    return s === 'pendiente' || s === 'pending';
  };

  const isPaid = (p) => {
    const s = String(p.status || '').toLowerCase();
    return s === 'pagado' || s === 'paid';
  };

  const formatCurrency = (value, currency = 'MXN') =>
    `$ ${Number(value || 0).toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;

  const sumByCurrency = (list, currency) =>
    list
      .filter((p) => (p.currency || 'MXN') === currency)
      .reduce((acc, p) => acc + Number(p.amount || 0), 0);

  const handleMarkAsPaid = async (payment) => {
    if (!onMarkAsPaid) return;
    setMarkingAsPaid((prev) => ({ ...prev, [payment.id]: true }));
    try {
      await onMarkAsPaid(payment);
      message.success('Pago marcado como pagado');
    } catch (error) {
      message.error(error?.message || 'Error al marcar como pagado');
    } finally {
      setMarkingAsPaid((prev) => ({ ...prev, [payment.id]: false }));
    }
  };

  const pendingPayments = payments.filter(isPending);
  const paidPayments   = payments.filter(isPaid);
  const hasUSD         = payments.some((p) => p.currency === 'USD');

  const pendingMXN = sumByCurrency(pendingPayments, 'MXN');
  const pendingUSD = sumByCurrency(pendingPayments, 'USD');
  const paidMXN    = sumByCurrency(paidPayments,   'MXN');
  const paidUSD    = sumByCurrency(paidPayments,   'USD');

  return (
    <Modal
      title={
        selectedDate
          ? `Detalles de Pagos - ${dayjs(selectedDate).format('DD/MM/YYYY')}`
          : 'Detalles de Pagos'
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>Cerrar</Button>,
      ]}
      className="payment-details-modal"
    >
      {!selectedDate ? (
        <Empty description="Selecciona una fecha para ver los pagos" />
      ) : payments.length === 0 ? (
        <Empty description="No hay pagos registrados para esta fecha" />
      ) : (
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>

          {Object.entries(groupedPayments).map(([type, typePayments]) => (
            <div key={type} style={{ marginBottom: 16 }}>
              {/* Encabezado de sección */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                background: '#f0f4ff',
                borderRadius: 6,
                marginBottom: 4,
                fontWeight: 600,
                color: '#1e3a8a',
                fontSize: 14,
              }}>
                <span>{typeIcons[type] || '📁'}</span>
                <span>{typeLabels[type] || type}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 500, color: '#64748b' }}>
                  {typePayments.length} registro{typePayments.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Filas de pagos */}
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                {typePayments.map((payment, idx) => (
                  <div
                    key={payment.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      borderBottom: idx < typePayments.length - 1 ? '1px solid #f0f0f0' : 'none',
                      borderLeft: `4px solid ${isPaid(payment) ? '#52c41a' : '#faad14'}`,
                      background: isPaid(payment) ? '#f6ffed' : '#fffbe6',
                    }}
                  >
                    {/* Nombre */}
                    <span style={{
                      flex: '1 1 200px',
                      minWidth: 0,
                      fontWeight: 600,
                      color: '#12213f',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {payment.provider || payment.concept || 'N/A'}
                    </span>

                    {/* Monto */}
                    <span style={{
                      flex: '0 0 190px',
                      fontWeight: 700,
                      color: '#0f172a',
                      whiteSpace: 'nowrap',
                    }}>
                      <DollarOutlined style={{ marginRight: 4, color: '#64748b' }} />
                      {formatCurrency(payment.amount, payment.currency || 'MXN')}
                    </span>

                    {/* Estado */}
                    <span style={{ flex: '0 0 100px', textAlign: 'center' }}>
                      <Tag
                        color={isPaid(payment) ? 'success' : 'warning'}
                        style={{ margin: 0, fontWeight: 600 }}
                      >
                        {isPaid(payment) ? 'PAGADO' : 'PENDIENTE'}
                      </Tag>
                    </span>

                    {/* Acción */}
                    <span style={{ flex: '0 0 150px', textAlign: 'right' }}>
                      {isPending(payment) ? (
                        <Button
                          type="primary"
                          size="small"
                          icon={<CheckCircleOutlined />}
                          loading={markingAsPaid[payment.id] || loading}
                          onClick={() => handleMarkAsPaid(payment)}
                          style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
                        >
                          Marcar Pagado
                        </Button>
                      ) : (
                        <span style={{
                          color: '#16a34a',
                          fontWeight: 600,
                          fontSize: 13,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}>
                          <CheckCircleOutlined /> Pagado
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Resumen */}
          <Divider style={{ margin: '16px 0 12px' }} />
          <div style={{
            display: 'flex',
            gap: 12,
            padding: '12px 16px',
            background: '#f8fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
          }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                Total
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1e3a8a' }}>
                {payments.length}
              </div>
            </div>

            <div style={{ width: 1, background: '#e2e8f0' }} />

            <div style={{ flex: 2, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#faad14', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                Pendiente ({pendingPayments.length})
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#faad14', whiteSpace: 'nowrap' }}>
                {formatCurrency(pendingMXN, 'MXN')}
              </div>
              {hasUSD && pendingUSD > 0 && (
                <div style={{ fontSize: 14, fontWeight: 500, color: '#faad14', whiteSpace: 'nowrap' }}>
                  {formatCurrency(pendingUSD, 'USD')}
                </div>
              )}
            </div>

            <div style={{ width: 1, background: '#e2e8f0' }} />

            <div style={{ flex: 2, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#16a34a', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                Pagado ({paidPayments.length})
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#16a34a', whiteSpace: 'nowrap' }}>
                {formatCurrency(paidMXN, 'MXN')}
              </div>
              {hasUSD && paidUSD > 0 && (
                <div style={{ fontSize: 14, fontWeight: 500, color: '#16a34a', whiteSpace: 'nowrap' }}>
                  {formatCurrency(paidUSD, 'USD')}
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </Modal>
  );
};

export default PaymentDetailsModal;
